import type { GhmSignalKind } from "@ghm/mqtt-contract";

import type { DataQuality, ResolvedWidgetValue, WidgetTone } from "./types";

/**
 * Regras de composição de um widget com N fontes.
 *
 * Duas perguntas, e nenhuma delas tem resposta no sufixo da tag:
 *
 *  1. **Estas fontes podem dividir o mesmo card/eixo?** O corte não é
 *     `_v` × `_acc` (restritivo e frouxo ao mesmo tempo: duas leituras `v` em
 *     V e °C não dividem eixo, e vazão `v` com volume `acc` do mesmo ponto é
 *     um painel de eixo duplo legítimo). É **mesma unidade + mesmo `kind`**.
 *
 *  2. **O que o card mostra quando 1 de 3 falha?** Se as três tensões viram um
 *     card só e L2 está em timeout, o card NÃO pode parecer saudável — mas
 *     também não pode virar "sem sinal", porque L1 e L3 estão lendo. O
 *     agregado leva o PIOR estado e diz quantas fontes estão afetadas.
 */

export interface SourceDescriptor {
  source: string;
  /** Unidade publicada no `/meta`. `undefined` = adimensional declarado. */
  unit?: string;
  kind?: GhmSignalKind;
}

/**
 * Unidade normalizada para comparação. Ausente e string vazia são a MESMA
 * coisa (adimensional); espaços nas pontas não distinguem grandeza.
 */
function normalizeUnit(unit?: string): string {
  return (unit ?? "").trim();
}

/**
 * Duas fontes podem ocupar o mesmo eixo? Exige unidade e semântica iguais.
 *
 * `kind` ausente em qualquer um dos lados é tratado como DESCONHECIDO e
 * bloqueia a combinação: sem saber o que os números significam, juntá-los no
 * mesmo eixo é palpite. Diferente do caso da matriz de compatibilidade, aqui
 * o permissivo não é o lado seguro — lá o risco era travar um widget que já
 * funcionava, aqui o risco é somar peras com maçãs num eixo só.
 */
export function canCombineSources(a: SourceDescriptor, b: SourceDescriptor): boolean {
  if (a.source === b.source) return false;
  if (!a.kind || !b.kind) return false;
  if (a.kind !== b.kind) return false;
  return normalizeUnit(a.unit) === normalizeUnit(b.unit);
}

/** Fontes candidatas que combinam com a âncora. */
export function combinableWith(
  anchor: SourceDescriptor,
  candidates: SourceDescriptor[],
): SourceDescriptor[] {
  return candidates.filter((candidate) => canCombineSources(anchor, candidate));
}

/**
 * Ordem de gravidade da qualidade. `offline` é o pior porque descreve o
 * equipamento inteiro; `stale` é pior que `waiting` porque waiting é o estado
 * normal de boot, e stale significa que havia leitura e parou.
 */
const QUALITY_SEVERITY: Record<DataQuality, number> = {
  live: 0,
  waiting: 1,
  stale: 2,
  offline: 3,
};

const TONE_SEVERITY: Record<WidgetTone, number> = {
  neutral: 0,
  ok: 1,
  info: 2,
  warn: 3,
  danger: 4,
};

export interface AggregatedSourceState {
  /** Pior qualidade entre as fontes. */
  quality: DataQuality;
  /** Pior tom entre as fontes — o card inteiro assume. */
  tone: WidgetTone;
  /** Quantas fontes têm leitura utilizável agora. */
  availableCount: number;
  /** Total de fontes do widget. */
  totalCount: number;
  /**
   * Diagnóstico da PRIMEIRA fonte afetada, para o card ter o que dizer sem
   * listar N mensagens. `null` quando nenhuma fonte tem diagnóstico.
   */
  diagnostic: ResolvedWidgetValue["diagnostic"] | null;
  /**
   * `true` quando parte das fontes lê e parte não. É o estado que o card
   * precisa comunicar de forma diferente de "tudo bem" e de "sem sinal":
   * um painel trifásico com L2 mudo está parcialmente cego, e esconder isso
   * atrás de dois valores bonitos é pior que não ter o painel.
   */
  partial: boolean;
}

/**
 * Estado do card a partir dos N valores resolvidos. Vazio devolve um agregado
 * neutro em vez de lançar — o painel pode renderizar antes das fontes
 * resolverem.
 */
export function aggregateSourceState(
  values: ResolvedWidgetValue[],
): AggregatedSourceState {
  if (values.length === 0) {
    return {
      quality: "waiting",
      tone: "neutral",
      availableCount: 0,
      totalCount: 0,
      diagnostic: null,
      partial: false,
    };
  }

  let quality: DataQuality = "live";
  let tone: WidgetTone = "neutral";
  let availableCount = 0;
  let diagnostic: ResolvedWidgetValue["diagnostic"] | null = null;

  for (const value of values) {
    const candidateQuality = value.quality ?? (value.isAvailable ? "live" : "waiting");
    if (QUALITY_SEVERITY[candidateQuality] > QUALITY_SEVERITY[quality]) {
      quality = candidateQuality;
    }
    if (TONE_SEVERITY[value.tone] > TONE_SEVERITY[tone]) {
      tone = value.tone;
    }
    if (value.isAvailable) availableCount += 1;
    if (!diagnostic && value.diagnostic) diagnostic = value.diagnostic;
  }

  return {
    quality,
    tone,
    availableCount,
    totalCount: values.length,
    diagnostic,
    partial: availableCount > 0 && availableCount < values.length,
  };
}

/**
 * Frase curta do estado parcial, para o card. `null` quando todas as fontes
 * estão lendo (o silêncio é a leitura correta de "está tudo bem") ou quando
 * nenhuma está — aí o estado de ausência normal já fala.
 */
export function describePartialState(state: AggregatedSourceState): string | null {
  if (!state.partial) return null;
  const missing = state.totalCount - state.availableCount;
  return missing === 1
    ? "1 das fontes sem leitura"
    : `${missing} das ${state.totalCount} fontes sem leitura`;
}
